import {
  Alert,
  Box,
  Button,
  FormField,
  Modal,
  SegmentedControl,
  SpaceBetween,
  Textarea,
} from '@cloudscape-design/components';
import { LotInspectionResults, LotStatus } from '@parallel-fluidics/shared/constants';
import { lotInspectionSchema } from '@parallel-fluidics/shared/models/lots';
import recordLotInspection from 'features/lots/api/recordLotInspection';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import { useUserData } from 'providers/userDataProvider';
import React, { useState } from 'react';
import { toast } from 'sonner';

const resultOptions = Object.values(LotInspectionResults)
  .map((result) => ({ text: result, id: result }));

/** One URL per line → trimmed, non-empty array. */
const splitLinks = (text) => text.split('\n').map((link) => link.trim()).filter(Boolean);

/**
 * Record a QC inspection for one lot: Passed seals a bag awaiting
 * inspection, Failed quarantines it. Data links point at measurement
 * data or photos, one URL per line.
 */
export default function RecordLotInspectionModal({ lot, onClose, onSuccess = () => {} }) {
  const { user } = useUserData();
  const [submitError, setSubmitError] = useState('');

  const {
    values,
    errors,
    isSubmitting,
    handleSubmit,
    setFieldValue,
  } = useFormik({
    initialValues: {
      result: LotInspectionResults.PASSED,
      note: '',
      dataLinks: '',
    },
    validateOnBlur: false,
    validateOnChange: false,
    validate: (validateValues) => {
      // Formik validates before onSubmit — clear the previous attempt's
      // submit error here so it can't linger next to validation errors.
      setSubmitError('');
      const validationErrors = {};
      try {
        // Validate the exact doc we'd write, so the form rules can never
        // drift from the shared schema's.
        lotInspectionSchema.validateSync({
          lotIds: [lot.id],
          result: validateValues.result,
          note: validateValues.note.trim(),
          dataLinks: splitLinks(validateValues.dataLinks),
          _createdBy: { uid: user.id, email: user.email },
        }, { abortEarly: false });
      } catch (validationError) {
        validationError.inner.forEach((issue) => {
          // "dataLinks[2]" -> the dataLinks field; keep the first error each.
          const [field] = issue.path.split(/[.[]/);
          if (!validationErrors[field]) validationErrors[field] = issue.message;
        });
      }
      return validationErrors;
    },
    onSubmit: async (submitValues) => {
      try {
        await recordLotInspection({
          lot,
          result: submitValues.result,
          note: submitValues.note.trim(),
          dataLinks: splitLinks(submitValues.dataLinks),
          user: { uid: user.id, email: user.email },
        });
        toast.success(`Inspection recorded for ${lot.code}`);
        onSuccess();
        onClose();
      } catch (error) {
        setSubmitError(error.message);
      }
    },
  });

  return (
    <Modal
      visible
      onDismiss={onClose}
      header={`Record inspection — ${lot.code}`}
      footer={(
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSubmit} loading={isSubmitting}>
              Record inspection
            </Button>
          </SpaceBetween>
        </Box>
      )}
    >
      <SpaceBetween direction="vertical" size="m">
        {submitError ? <Alert type="error">{submitError}</Alert> : null}
        <FormField
          label="Result"
          description={lot.status === LotStatus.AWAITING_INSPECTION
            ? 'Passed seals the bag; Failed quarantines it.'
            : 'Failed quarantines the bag; Passed keeps its current status.'}
        >
          <SegmentedControl
            selectedId={values.result}
            options={resultOptions}
            onChange={({ detail }) => setFieldValue('result', detail.selectedId)}
          />
        </FormField>
        <FormField label="Note" errorText={errors.note}>
          <Textarea
            rows={2}
            value={values.note}
            onChange={({ detail }) => setFieldValue('note', detail.value)}
          />
        </FormField>
        <FormField
          label="Data links"
          description="Measurement data, photos, reports. One URL per line."
          errorText={errors.dataLinks}
        >
          <Textarea
            rows={2}
            value={values.dataLinks}
            onChange={({ detail }) => setFieldValue('dataLinks', detail.value)}
          />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
}

RecordLotInspectionModal.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  lot: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};
